import { Box, Stack, SxProps, Typography } from "@mui/material";
import { useLocation } from "@reach/router";
import { StaticImage } from "gatsby-plugin-image";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useEffect } from "react";
import { l_prevNextCS } from "./locales";

import ButtonWithUnderlineWithIcon from "./components/ButtonWithUnderlineWithIcon";

const PrevNextCaseStudy = ({ sx }: { sx?: SxProps }) => {
  const location = useLocation();

  const paths = [
    {
      link: "/works/district",
      image: "case-studies-preview/district_preview.png",
    },
    {
      link: "/works/holding-moda",
      image: "case-studies-preview/hm_preview.png",
    },
    {
      link: "/works/angeleye",
      image: "case-studies-preview/angeleye_preview.png",
    },
    {
      link: "/works/joule",
      image: "case-studies-preview/joule_preview.png",
    },
    {
      link: "/works/bonusx",
      image: "case-studies-preview/bonus-x-preview.png",
    },
    {
      link: "/works/intesa-sanpaolo-assicura/",
      image: "case-studies-preview/isatra-preview.png",
    },
    {
      link: "/works/startup-geeks",
      image: "case-studies-preview/sgeeks-preview.png",
    },
    {
      link: "/works/nowr",
      image: "case-studies-preview/nowr-preview.png",
    },
    {
      link: "/works/belvillage",
      image: "case-studies-preview/belvillage-preview.png",
    },
  ].filter(Boolean) as { link: string; image: string }[];

  const currentPathIndex = paths
    .map((path) => path.link)
    .findIndex((path) => location.pathname.includes(path));

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    gsap.fromTo(
      ".blue-circle-prev-next-cs",
      {
        x: 20,
        y: -60,
      },
      {
        scrollTrigger: {
          trigger: ".blue-circle-prev-next-cs",
          scrub: 1,
        },
        x: 0,
        y: 0,
      },
    );
    gsap.fromTo(
      ".orange-lemon-prev-next-cs",
      {
        x: -50,
        y: -20,
        rotate: 90,
      },
      {
        scrollTrigger: {
          trigger: ".orange-lemon-prev-next-cs",
          scrub: 1,
        },
        x: 0,
        y: 0,
        rotate: 137,
      },
    );
    gsap.fromTo(
      ".bicolor-square-prev-next-cs",
      {
        x: -70,
        rotate: -80,
      },
      {
        scrollTrigger: {
          trigger: ".orange-lemon-prev-next-cs",
          scrub: 1,
        },
        x: 0,
        rotate: 40,
      },
    );
    return () => {};
  }, []);

  return (
    <Box
      sx={{
        width: "90%",
        maxWidth: "1400px",
        margin: "auto",
        marginTop: { md: "200px", xs: "80px" },
        marginBottom: { md: "200px", xs: "80px" },
        position: "relative",
        ...sx,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontSize: { md: "48px", xs: "20px" },
          lineHeight: { md: "52px", xs: "24px" },
          fontWeight: 700,
          textAlign: "center",
          marginBottom: { md: "180px", xs: "60px" },
        }}
      >
        {l_prevNextCS("prevNextCS.title")}
      </Typography>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          width: "80%",
          margin: "auto",
        }}
      >
        <ButtonWithUnderlineWithIcon
          caseStudy={paths[currentPathIndex - 1] ?? paths[paths.length - 1]}
        >
          <Box sx={{ width: { md: "40px", xs: "20px" } }}>
            <StaticImage src="./images/prevCircleArrow.svg" alt="" />
          </Box>
          <Typography
            sx={{
              fontSize: { md: "48px", xs: "20px" },
              lineHeight: { md: "52px", xs: "24px" },
            }}
          >
            {l_prevNextCS("prevNextCS.prev")}
          </Typography>
        </ButtonWithUnderlineWithIcon>

        <ButtonWithUnderlineWithIcon
          caseStudy={paths[currentPathIndex + 1] ?? paths[0]}
        >
          <Typography
            sx={{
              fontSize: { md: "48px", xs: "20px" },
              lineHeight: { md: "52px", xs: "24px" },
            }}
          >
            {l_prevNextCS("prevNextCS.next")}
          </Typography>
          <Box sx={{ width: { md: "40px", xs: "20px" } }}>
            <StaticImage src="./images/nextCircleArrow.svg" alt="" />
          </Box>
        </ButtonWithUnderlineWithIcon>
      </Stack>

      <Box
        sx={{
          position: "absolute",
          width: { md: "32px", xs: "20px" },
          top: 0,
          left: "5%",
        }}
        className="blue-circle-prev-next-cs"
      >
        <StaticImage alt="" src="./images/blue-circle-prev-next-cs.svg" />
      </Box>
      <Box
        sx={{
          position: "absolute",
          width: { md: "60px", xs: "40px" },
          top: "-20%",
          right: "10%",
          transform: "rotate(137deg)",
        }}
        className="orange-lemon-prev-next-cs"
      >
        <StaticImage alt="" src="./images/orange-lemon-prev-next-cs.svg" />
      </Box>
      <Box
        sx={{
          position: "absolute",
          width: { md: "40px", xs: "25px" },
          bottom: "-30%",
          right: "50%",
          transform: "rotate(40deg)",
        }}
        className="bicolor-square-prev-next-cs"
      >
        <StaticImage alt="" src="./images/bicolor-square-prev-next-cs.svg" />
      </Box>
    </Box>
  );
};

export default PrevNextCaseStudy;
